/**
 * ScorePill — the 0–100 lead score in its band colour.
 *
 * Sized to stay legible at 11px in both themes: tabular numerals so a column of
 * scores aligns digit-for-digit, and a foreground mixed toward the theme's text
 * colour so no band falls under 4.5:1 on its own tint — the light theme is the
 * binding case, and `SCORE_TEXT_MIX` is the weight that clears it for all four
 * bands on both `--surface-1` and `--surface-2`. `dashboard/scripts/contrast-check.mjs`
 * asserts it; do not raise the number without re-running that.
 *
 * FOUR BANDS, FOUR COLOURS. The pill used to carry the hue only as a 14% wash
 * plus a mixed foreground, and at that strength `--band-early` and `--band-cold`
 * — a slate and a mist one ramp step apart — painted the same grey: a 48, a 36
 * and a 34 were indistinguishable, so a four-band vocabulary had three colours
 * behind it. The band now shows at three strengths at once:
 *
 *   rail    `var(--band-*)` at full saturation, 2px, full height. Two gates hold
 *           this mark up, and they answer different questions. DISTINCTNESS: at
 *           2px a reader has raw channel difference and nothing else, so every
 *           pair in the ramp must clear 20 on at least TWO of R/G/B — the
 *           one-channel version of the rule passed dark early against dark cold
 *           at (15, 23, 32), two desaturated slate-blues that painted one grey.
 *           ORDER: `ratio(band, --surface-1)` climbs monotonically cold → early
 *           → warm → hot in BOTH themes, because a ramp that inverts with the
 *           theme aims the eye at the dead leads on one of them. Measured —
 *           dark 3.24 / 4.44 / 9.69 / 11.46, light 3.47 / 4.76 / 5.69 / 7.12.
 *           Both are asserted in `dashboard/scripts/contrast-check.mjs`.
 *   edge    the hue at `SCORE_EDGE_MIX` over the row — a visible band-coloured
 *           hairline rather than a neutral one.
 *   fill    the `--band-*-soft` pair, which is exactly the hue at
 *           `SCORE_FILL_MIX` or less. The contrast gate models the strongest of
 *           the four, so every band clears it.
 *
 * The hue itself comes from `BAND_META`, the one place the ramp is declared, and
 * the band a score falls in comes from `bandForScore` in `@shared/types`, the
 * one place the cut-offs are declared. The pill, the card's band edge, the score
 * breakdown and the server's stored `scoreBand` cannot drift apart.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import type { ScoreBand } from '@shared/types';
import { cn } from '@/lib/cn';
import { BAND_META, bandForScore } from '@/lib/constants';
import { mixSurface, mixText } from './Badge';

/** Percentage of the band hue kept before mixing in `--text-primary`. */
export const SCORE_TEXT_MIX = 64;
/**
 * Strongest fill any band paints, as a share of the hue over the card surface.
 * The `--band-*-soft` tokens are the hue at 10–16%; this is the top of that
 * range, so it is the case the contrast gate has to clear.
 */
export const SCORE_FILL_MIX = 16;
/** The hairline around the pill — band-coloured, not neutral. */
export const SCORE_EDGE_MIX = 56;

/** Outer shell: height and type size. Padding lives on the content span. */
const SIZE: Record<'sm' | 'md', string> = {
  // 12px (`--text-sm`), not 11. In the inbox row the score sat at 11px beside a
  // 12px REF, a 12px SLA and a 12px CREATED — the numeric column the surface is
  // built around was the smallest glyph in its own row, and the one carrying the
  // least contrast headroom. It now shares the row's small step with the other
  // three numerics; the 16px shell is unchanged, so no geometry moves.
  sm: 'h-4 text-sm',
  md: 'h-5 text-xs',
};

/** Inner track: the padding the rail must not eat into. */
const PAD: Record<'sm' | 'md', string> = {
  sm: 'gap-1 pr-1 pl-0.5',
  md: 'gap-1.5 pr-1.5 pl-1',
};

/** `bare` drops the padding with the chrome — the number sits on the row. */
const BARE_PAD: Record<'sm' | 'md', string> = {
  sm: 'gap-1 pl-1',
  md: 'gap-1.5 pl-1.5',
};

export interface ScorePillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  score: number;
  /**
   * Optional. Omitted, it is DERIVED from `score` through `bandForScore` — the
   * one place the cut-offs live. Callers used to pass `inquiry.scoreBand` (the
   * server's stored value) on some surfaces and `bandForScore(score)` on others,
   * which is two rules for one colour: for the seconds between an optimistic
   * score change and the write coming back, the same number wore two different
   * band hues depending on which screen you were on. Pass it only to render a
   * band that is not this number's own.
   */
  band?: ScoreBand;
  /** Adds the 4px micro-bar that shows where the score sits in 0–100. */
  showBar?: boolean;
  /** Spell the band out beside the number. */
  showBand?: boolean;
  size?: 'sm' | 'md';
  /**
   * `chip` — border, tint and rail. Right for a card, a record header or
   * anywhere a single score has to hold its own against unrelated content.
   *
   * `bare` — the rail alone, against a tabular `--text-primary` number. Right
   * for a *column* of scores: at 22×16 the chip wrapped 12px of glyph in four
   * pieces of chrome and the column read as a row of boxes rather than a row of
   * numbers, while Warm, Early and Cold still resolved to one grey-blue at
   * scanning speed. The rail is the mark that actually separates the four bands
   * (it is channel-gated ≥20 on two of three); the border and the 14–16% wash were not
   * carrying the difference, only the weight.
   */
  variant?: 'chip' | 'bare';
  /**
   * Paint the band rail. Additive, defaults to `true`, so every existing call
   * site is byte-identical.
   *
   * Pass `false` where the hue would cost more than it earns. The board's card
   * is the case that forced it: `--band-hot` is a rung of the gold ramp
   * (`--gold-300` dark, `--gold-800` light — 1.0° of hue from `--accent`), and
   * on a board sorted score-descending 71 of 175 cards wore it, so the product's
   * single create colour was also the commonest mark on 41% of the primary
   * object. The rail was buying one bit while it was at it: the same 175 cards
   * painted exactly two of the four band values, because a 25-card page ordered
   * by score never reaches Early or Cold. The number 4px to its right is exact,
   * sorted, and already carries the band in `title` and in its `sr-only`
   * sentence, so on that surface the number carries the band alone.
   */
  rail?: boolean;
}

export const ScorePill = forwardRef<HTMLSpanElement, ScorePillProps>(function ScorePill(
  {
    score,
    band,
    showBar = false,
    showBand = false,
    size = 'md',
    variant = 'chip',
    rail = true,
    className,
    style,
    ...rest
  },
  ref,
) {
  const resolved = band ?? bandForScore(score);
  const meta = BAND_META[resolved];
  const hue = meta.accent;
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const bare = variant === 'bare';
  /*
    ONE FACT, ONE SENTENCE. The pointer tooltip read "Score 86 of 100 · Hot"
    and the screen-reader text read "Score 86 out of 100, Hot" — the same fact
    in two dialects, both of which had to be kept true separately. It is built
    once here and spent in both places, the way `describeColumnCount` already
    does for the board's column heads.
  */
  const sentence = `Score ${clamped} of 100 · ${meta.label}`;

  return (
    <span
      ref={ref}
      title={sentence}
      className={cn(
        'inline-flex shrink-0 items-stretch overflow-hidden',
        // The chip is a chip; the bare form is a rule beside a number, and a
        // rounded 2px rail beside two digits reads as an opening bracket.
        bare ? 'rounded-none' : 'rounded-sm border',
        'font-medium whitespace-nowrap',
        SIZE[size],
        className,
      )}
      style={{
        background: bare ? undefined : meta.soft,
        borderColor: bare ? undefined : mixSurface(hue, SCORE_EDGE_MIX),
        color: bare ? 'var(--text-primary)' : mixText(hue, SCORE_TEXT_MIX),
        ...style,
      }}
      {...rest}
    >
      {/* The band at full strength. Four bands, four unmistakable marks.

          AND IT IS NOT DRAWN EVERYWHERE ANY MORE — see `rail`. The top rung of
          this ramp (`--band-hot`) is a rung of the gold ramp (`--gold-300`
          dark, `--gold-800` light), 1.0° of hue from `--accent`, and the
          product's rule is that gold means create and nothing else. On a
          surface that paints hundreds of these at once the exemption stopped
          being an exemption and became a second meaning: the board's default
          view put that hue on 41% of its cards and on the one control that
          creates. The mark is worth its hue where a score appears once or
          twice on a screen — a record header, a company row — and the surfaces
          that spend it in bulk turn it off rather than the token being widened
          into a licence.

          `data-accent-role` stays on the mark, so a DOM sweep that finds it
          finds a declaration rather than an accident. */}
      {rail ? (
        <span
          aria-hidden="true"
          data-accent-role="score-band"
          className="w-0.5 shrink-0"
          style={{ background: hue }}
        />
      ) : null}

      <span
        className={cn(
          'inline-flex items-center leading-none tnum',
          bare ? BARE_PAD[size] : PAD[size],
        )}
      >
        <span aria-hidden="true">{clamped}</span>
        <span className="sr-only">{sentence}</span>

        {showBand ? (
          <span aria-hidden="true" className="font-medium opacity-80">
            {meta.label}
          </span>
        ) : null}

        {showBar ? (
          <span
            aria-hidden="true"
            className="h-1 w-6 shrink-0 overflow-hidden rounded-full"
            style={{ background: mixSurface(hue, 22) }}
          >
            <span
              className="block h-full rounded-full"
              style={{ width: `${clamped}%`, background: hue }}
            />
          </span>
        ) : null}
      </span>
    </span>
  );
});
